
import { Component, ElementRef, inject, model, ViewChild, computed, EffectRef, effect, DestroyRef } from '@angular/core';
import { toObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AppBreadcrumbComponent } from './app.breadcrumb.component';
import { DrawerModule } from 'primeng/drawer';
import { ButtonModule } from 'primeng/button';
import { Router } from '@angular/router';
import { TooltipModule } from 'primeng/tooltip';
import { BadgeModule } from 'primeng/badge';
import { LayoutService } from './service/app.layout.service';
import { AuthService } from '@lib/auth/data-access';
import { TenantService } from '@lib/tenant/data-access';
import { AvatarModule } from 'primeng/avatar';
import { PersonService } from '@lib/person/data-access';
import { DigitalMediaService } from '@lib/digital-media/data-access';
import { DigitalMediaEntityType } from '@lib/digital-media/models';
import { catchError, forkJoin, map, of, switchMap } from 'rxjs';
import { NotificationBellComponent } from '@lib/notification/feature-bell';
import { BroadcastService, EnvironmentService } from '@lib/shared/data-access';
import { BroadcastChannelType } from '@lib/shared/models';
import { PopoverModule } from 'primeng/popover';

@Component({
    selector: 'app-topbar',
    templateUrl: './app.topbar.component.html',
    standalone: true,
    imports: [
    AppBreadcrumbComponent,
    DrawerModule,
    ButtonModule,
    TooltipModule,
    BadgeModule,
    AvatarModule,
    NotificationBellComponent,
    PopoverModule
]
})
export class AppTopbarComponent {
    @ViewChild('menubutton') menuButton!: ElementRef;
    public authService = inject(AuthService);
    public layoutService = inject(LayoutService);
    public tenantService = inject(TenantService);
    public environmentService = inject(EnvironmentService);
    public router = inject(Router);
    public isSuperAdminScreen = model.required<boolean>();
    private personService = inject(PersonService);
    private digitalMediaService = inject(DigitalMediaService);
    private destroyRef = inject(DestroyRef);
    private broadcastService = inject(BroadcastService);

    public currentUserDetails = this.authService.authUserComputedSignal();

    private readonly fetchEmployeeEffect: EffectRef = effect(() => {
        this.personService.getEmployeeIdForLoggedInUser().subscribe();
      });

    public personId = computed(() => {
        const employee = this.personService.loggedInEmployeeComputedSignal();
        return employee?.personId;
    });

    public personMedia = toObservable(this.personId).pipe(
        switchMap((personId) => {
            if (!personId) return of([]);
            return this.digitalMediaService
                .getDigitalMediasByEntityIdAndEntityType(personId, DigitalMediaEntityType.Persons)
                .pipe(
                    switchMap((mediaList) => {
                        if (mediaList.length === 0) return of([]);
                        const enriched = mediaList.map(media =>
                            this.digitalMediaService.getDigitalMediaSecuredUrl(media.mediaId).pipe(
                                map(secure => ({ ...media, url: secure.url })),
                                catchError(() => of({ ...media, url: null }))
                            )
                        );
                        return forkJoin(enriched);
                    })
                );
        }),
        catchError(() => of([]))
    );

    public avatarImageUrl?: string;

    constructor() {
        this.personMedia
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((mediaList) => {
            const firstImage = mediaList.find(m => !!m.url);
            if (firstImage) {
              this.avatarImageUrl = firstImage.url!;
            }
          });
      }
        
    navigate(route: string): void {
        this.router.navigate([route]);
    }

    onMenuButtonClick() {
        this.layoutService.onMenuToggle();
    }

    onProfileButtonClick() {
        this.layoutService.showProfileSidebar();
    }

    onConfigButtonClick() {
        this.layoutService.showConfigSidebar();
    }

    onSelectTenantButtonClick() {
        this.tenantService.showTenantSelectionSidebar();
    }

    onSuperAdminButtonClick() {
        this.router.navigate(['/super-admin']);
    }

    onBackToAdministrationCenterButtonClick() {
        this.router.navigate(['/administration-center']);
    }

    logout() {
        this.authService.logout();        
        
        //Let the other tabs and windows know that the user has logged out
        this.broadcastService.postMessage(BroadcastChannelType.Logout, 'Logout');
    }

    public headerComputed = computed(() => {
        const tenantInUse = this.authService.userPrincipalComputedSignal()?.accessibleTenants.find(tenant => tenant.externalId === this.authService.currentTenantExternalIdComputedSignal());
        return tenantInUse?.tenantName ?? '';
    });

    public isNonLiveEnvironmentComputed = computed(() => {
        const tenantInUse = this.authService.userPrincipalComputedSignal()?.accessibleTenants.find(tenant => tenant.externalId === this.authService.currentTenantExternalIdComputedSignal());
        const name = tenantInUse?.tenantName?.toLowerCase() ?? '';
        const isNonLive = name.includes('training') || name.includes('integration') || name.includes('conversion');
        return isNonLive;
    });
    
    public nonLiveEnvironmentComputed = computed(() => {
        const tenantInUse = this.authService.userPrincipalComputedSignal()?.accessibleTenants.find(tenant => tenant.externalId === this.authService.currentTenantExternalIdComputedSignal());

        const name = tenantInUse?.tenantName?.toLowerCase() ?? '';

        if (name.includes('training')) return 'Training';
        if (name.includes('integration')) return 'Integration';
        if (name.includes('conversion')) return 'Conversion';

        return '';
    });
}